import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Slider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSettings } from '../contexts/SettingsContext';
import { useConfig } from '../hooks/useConfig';

const SettingsModal = ({ open, onClose, models = [] }) => {
  const { settings, updateSettings, getCategoryPersonalities, hasMultipleCategories } = useSettings();
  const { categories } = useConfig();
  
  // Local state for modal (allows cancel functionality)
  const [localSettings, setLocalSettings] = useState(settings);
  
  // Update local state when modal opens or settings change
  useEffect(() => {
    if (open) {
      setLocalSettings(settings);
    }
  }, [open, settings]);
  
  const handleApply = () => {
    updateSettings(localSettings);
    onClose();
  };
  
  const handleCancel = () => {
    setLocalSettings(settings); // Reset to original
    onClose();
  };
  
  const handleLocalChange = (key, value) => {
    if (key === 'category') {
      // When category changes, reset personality to first in new category
      const categoryPersonalities = getCategoryPersonalities(value);
      const newPersonality = categoryPersonalities.length > 0 ? categoryPersonalities[0].name : '';
      setLocalSettings(prev => ({
        ...prev,
        category: value,
        personality: newPersonality
      }));
    } else {
      setLocalSettings(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  // Format cost helper function
  const formatCost = (cost) => {
    if (typeof cost === 'number') {
      return `$${cost.toFixed(4)}`;
    }
    return cost || 'N/A';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleApply} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '300px' }
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Settings
        <IconButton
          onClick={handleCancel}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1, width: '100%' }}>
          {/* Model Selection */}
          <FormControl fullWidth>
            <InputLabel>Model</InputLabel>
            <Select
              value={localSettings.model || ''}
              label="Model"
              onChange={(e) => handleLocalChange('model', e.target.value)}
            >
              {models.length > 0 ? (
                models.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.id} - in {formatCost(model.input_rate)}/1K, out {formatCost(model.output_rate)}/1K
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="gpt-4-mini">
                  gpt-4-mini - Loading...
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Category Selection - only show if multiple categories exist */}
          {hasMultipleCategories() && (
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={localSettings.category || ''}
                label="Category"
                onChange={(e) => handleLocalChange('category', e.target.value)}
              >
                {Object.keys(categories || {}).map((categoryKey) => (
                  <MenuItem key={categoryKey} value={categoryKey}>
                    {categories[categoryKey].name || categoryKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Personality Selection */}
          <FormControl fullWidth>
            <InputLabel>Personality</InputLabel>
            <Select
              value={localSettings.personality || ''}
              label="Personality"
              onChange={(e) => handleLocalChange('personality', e.target.value)}
            >
              {getCategoryPersonalities(localSettings.category).map((personality) => (
                <MenuItem key={personality.name} value={personality.name}>
                  {personality.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Tone Selection */}
          <Box sx={{ width: '100%', px: 1 }}>
            <Typography gutterBottom>
              Tone
            </Typography>
            <Slider
              value={localSettings.tone || 3}
              onChange={(e, value) => handleLocalChange('tone', value)}
              min={1}
              max={5}
              step={1}
              marks={[
                { value: 1, label: 'Stern' },
                { value: 2, label: 'Professional' },
                { value: 3, label: 'Fair' },
                { value: 4, label: 'Friendly' },
                { value: 5, label: 'Very Kind' }
              ]}
              valueLabelDisplay="auto"
              sx={{ 
                mt: 1, 
                mb: 2,
                width: '100%',
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  transform: 'translateX(-50%)',
                  maxWidth: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          </Box>
          
          {/* Response Length Selection */}
          <Box sx={{ width: '100%', px: 1 }}>
            <Typography gutterBottom>
              Response length
            </Typography>
            <Slider
              value={localSettings.responseLength || 3}
              onChange={(e, value) => handleLocalChange('responseLength', value)}
              min={1}
              max={5}
              step={1}
              marks={[
                { value: 1, label: 'Very short' },
                { value: 2, label: 'Short' },
                { value: 3, label: 'Medium' },
                { value: 4, label: 'Long' },
                { value: 5, label: 'Very long' }
              ]}
              valueLabelDisplay="auto"
              sx={{ 
                mt: 1, 
                mb: 2,
                width: '100%',
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  transform: 'translateX(-50%)',
                  maxWidth: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          </Box>
          
          {/* Info text */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Settings changes take effect immediately when you click Apply.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
